import { useEffect, useState } from 'react'
import { adminApi, ApiError, type PlatformSettings as Settings } from '../lib/api'
import { testBrowserKey } from '../lib/basemap'

type KeyName = 'google_server_key' | 'google_browser_key'
type Outcome = { ok: boolean; detail: string }

/** The two keys, with what each is for and how Google should restrict it.
 *
 *  THE RESTRICTION IS SAID HERE, beside the box, rather than left for somebody
 *  to remember in the Google Cloud console: an unrestricted key that leaks is
 *  somebody else spending the platform's Google account. */
const KEYS: {
  name: KeyName
  has: 'has_google_server_key' | 'has_google_browser_key'
  label: string
  use: string
  restrict: string
}[] = [
  {
    name: 'google_server_key',
    has: 'has_google_server_key',
    label: 'Google server key',
    use: 'Used by the CRM server for address suggestions (Places Autocomplete), '
      + 'resolving a picked suggestion (Place Details) and lookups (Geocoding API).',
    restrict: 'In Google Cloud, restrict it to the IP address 216.128.176.101 '
      + 'and to the Places API and the Geocoding API.',
  },
  {
    name: 'google_browser_key',
    has: 'has_google_browser_key',
    label: 'Google browser key',
    use: 'Loaded by dispatchers’ browsers to draw Google maps (Maps JavaScript '
      + 'API), for customers on Level B only.',
    restrict: 'In Google Cloud, restrict it to the websites '
      + 'https://*.crm.advancetalk.ca/* and to the Maps JavaScript API.',
  },
]

/** Settings above every customer: today, the platform's pair of Google keys.
 *
 *  THE ONE DELIBERATE EXCEPTION TO "EVERY CUSTOMER IS SEPARATELY WIRED". The
 *  platform holds the Google account and resells map levels, so one pair of
 *  keys serves every customer sold a Google level -- unless that customer has
 *  his own on his Map tab, which is then used instead.
 *
 *  KEYS GO IN AND NEVER COME BACK, the browser key included: the page is told
 *  whether each is set. So a browser key is tried AS IT IS TYPED, in this
 *  browser, before it is saved -- the server cannot try it (Google refuses a
 *  website-restricted key from a server) and this page never holds a stored
 *  one to try. */
export function PlatformSettings({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<Settings | null>(null)
  const [drafts, setDrafts] = useState<Record<KeyName, string>>({
    google_server_key: '', google_browser_key: '',
  })
  // Which stored keys the operator has asked to type over. A stored key is
  // shown filled and locked, for the Connections cards' reason: an empty box
  // beside a working key reads as "not set".
  const [replacing, setReplacing] = useState<Record<KeyName, boolean>>({
    google_server_key: false, google_browser_key: false,
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [serverTest, setServerTest] = useState<Outcome | null>(null)
  const [browserTest, setBrowserTest] = useState<Outcome | null>(null)

  useEffect(() => {
    adminApi
      .platformSettings()
      .then(setState)
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : 'Could not load platform settings.'))
  }, [])

  const typed = (name: KeyName) => drafts[name].trim()
  const dirty = KEYS.some((k) => typed(k.name))

  function settle(next: Settings) {
    setState(next)
    setDrafts({ google_server_key: '', google_browser_key: '' })
    setReplacing({ google_server_key: false, google_browser_key: false })
    setError(null)
  }

  async function save() {
    const body: { google_server_key?: string; google_browser_key?: string } = {}
    for (const k of KEYS) if (typed(k.name)) body[k.name] = typed(k.name)
    setBusy(true)
    try {
      settle(await adminApi.platformSettingsSave(body))
      // A test of the key that was just replaced says nothing about the new one.
      if (body.google_server_key) setServerTest(null)
      if (body.google_browser_key) setBrowserTest(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  async function clear(name: KeyName, label: string) {
    if (
      !confirm(
        `Clear the platform’s ${label}?\n\nEvery customer on a ` +
        'Google level without a key of his own stops getting Google from the ' +
        'next request: suggestions fall back to his own address book and ' +
        (name === 'google_browser_key'
          ? 'Level B maps fall back to OpenStreetMap.'
          : 'lookups return a sentence instead of a position.'),
      )
    ) return
    setBusy(true)
    try {
      settle(await adminApi.platformSettingsSave({ clear: [name] }))
      if (name === 'google_server_key') setServerTest(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not clear that key.')
    } finally {
      setBusy(false)
    }
  }

  async function testServer() {
    setServerTest({ ok: false, detail: 'Testing…' })
    try {
      setServerTest(await adminApi.platformSettingsTest())
      setState(await adminApi.platformSettings())
    } catch (e) {
      setServerTest({
        ok: false, detail: e instanceof ApiError ? e.message : 'Test failed.',
      })
    }
  }

  async function testBrowser() {
    setBrowserTest({ ok: false, detail: 'Testing in this browser…' })
    setBrowserTest(await testBrowserKey(typed('google_browser_key')))
  }

  const when = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
      : null

  return (
    <div className="pad">
      <h2>Platform settings</h2>
      <p className="blurb">
        Above every customer. These Google keys are used for every customer
        sold a Google map level — A + Google search boost, or B — unless that
        customer has keys of his own on his Map tab, which are then used
        instead. They are the one thing on this console that is not wired per
        customer. Stored encrypted; this page is only ever told whether each
        is set.
      </p>

      {error && <div className="notice">{error}</div>}
      {state && !state.secrets_available && (
        <div className="notice">SECRET_KEY is not set, so no key can be stored.</div>
      )}

      {state && (
        <>
          {KEYS.map((k) => {
            const isSet = state[k.has]
            const locked = isSet && !replacing[k.name]
            return (
              <div className="field" key={k.name}>
                <label>
                  {k.label}
                  {isSet && <span className="isset"> — stored</span>}
                </label>
                <div className="fieldrow">
                  <input
                    type="password"
                    autoComplete="off"
                    value={locked ? '••••••••••••••••' : drafts[k.name]}
                    readOnly={locked}
                    placeholder={isSet ? 'leave blank to keep' : 'not set'}
                    onChange={(e) => setDrafts({ ...drafts, [k.name]: e.target.value })}
                  />
                  {isSet && (
                    <button
                      className="ghost tiny"
                      type="button"
                      onClick={() => {
                        setReplacing({ ...replacing, [k.name]: !replacing[k.name] })
                        setDrafts({ ...drafts, [k.name]: '' })
                      }}
                    >
                      {locked ? 'Replace' : 'Keep stored'}
                    </button>
                  )}
                  {isSet && locked && (
                    <button className="ghost tiny danger" type="button" disabled={busy}
                            onClick={() => clear(k.name, k.label)}>
                      Clear
                    </button>
                  )}
                  {/* ONLY A TYPED BROWSER KEY CAN BE TRIED, see above. */}
                  {k.name === 'google_browser_key' && typed(k.name) && (
                    <button className="ghost tiny" type="button" onClick={testBrowser}>
                      Test in this browser
                    </button>
                  )}
                </div>
                <div className="dim" style={{ marginTop: 5, maxWidth: '72ch', lineHeight: 1.5 }}>
                  {k.use} {k.restrict}
                </div>
                {k.name === 'google_browser_key' && browserTest && (
                  <div className={`testresult ${browserTest.ok ? 'ok' : 'bad'}`}
                       style={{ marginTop: 5 }}>
                    {browserTest.detail}
                  </div>
                )}
              </div>
            )
          })}

          <p className="connstatus">
            {state.updated_at
              ? `Last changed ${when(state.updated_at)}${state.updated_by ? ` by ${state.updated_by}` : ''}.`
              : 'No platform keys have been saved yet.'}
            {state.last_test_at && (
              <>
                {' '}Server key last tested {when(state.last_test_at)}:{' '}
                {state.last_test_ok ? 'working.' : (state.last_test_detail || 'not working.')}
              </>
            )}
            {' '}A browser key is tested as it is typed; once saved, it is
            proven by opening a map on a Level B customer’s screen.
          </p>

          <div className="btnrow">
            <button className="primary compact" disabled={busy || !dirty} onClick={save}>
              Save
            </button>
            <button
              className="ghost"
              // THE STORED KEY is what the server tests. A key typed and not
              // yet saved would be reported on as though it had been -- and the
              // reason it is disabled is said BESIDE it, below, not in a title
              // nobody hovers.
              disabled={!state.has_google_server_key || !!typed('google_server_key')}
              onClick={testServer}
            >
              Test the server key
            </button>
            <button className="ghost" onClick={onClose}>
              Back
            </button>
            {serverTest && (
              <span className={`testresult ${serverTest.ok ? 'ok' : 'bad'}`}>
                {serverTest.detail}
              </span>
            )}
          </div>
          {typed('google_server_key') && state.has_google_server_key && (
            <p className="footnote">
              Save first — Test uses the stored server key, not the one typed.
            </p>
          )}
        </>
      )}
    </div>
  )
}
